<template>
  <div class="twinfield-manage">
    <div v-if="loading" class="twinfield-manage__loading">
      <LoadingSpinner />
    </div>

    <template v-else>
      <div class="twinfield-manage__banner" :class="bannerClass">
        <BaseIcon :name="bannerIcon" size="14" />
        <span>{{ bannerMessage }}</span>
      </div>

      <p v-if="status.organisation_name || status.office_name" class="twinfield-manage__company">
        <template v-if="status.office_name">
          {{ $t('twinfield.settings.office') }}:
          <strong>{{ status.office_name }}</strong>
        </template>
        <template v-else-if="status.organisation_name">
          {{ status.organisation_name }}
        </template>
      </p>

      <div v-if="!isConnected" class="twinfield-manage__actions">
        <div />
        <Btn variant="primary" :loading="connecting" @click="handleConnect">
          {{ $t('twinfield.actions.connect') }}
        </Btn>
      </div>

      <template v-else>
        <div class="twinfield-manage__fields">
          <div>
            <SelectInput
              id="twinfield-office"
              :label="$t('twinfield.settings.office')"
              :model-value="form.office_code"
              :options="officeOptions"
              :disabled="officeOptions.length <= 1"
              @update:model-value="handleOfficeChange"
            />
            <p class="twinfield-manage__toggle-hint">
              {{ $t('twinfield.settings.office_info') }}
            </p>
          </div>

          <SelectInput
            id="twinfield-grootboek-hoog"
            :label="$t('twinfield.settings.grootboek_hoog')"
            v-model="form.grootboek_omzet_hoog"
            :options="ledgerOptions"
            :disabled="!form.office_code"
          />
          <SelectInput
            id="twinfield-grootboek-laag"
            :label="$t('twinfield.settings.grootboek_laag')"
            v-model="form.grootboek_omzet_laag"
            :options="ledgerOptions"
            :disabled="!form.office_code"
          />
          <SelectInput
            id="twinfield-grootboek-nul"
            :label="$t('twinfield.settings.grootboek_nul')"
            v-model="form.grootboek_omzet_nul"
            :options="ledgerOptions"
            :disabled="!form.office_code"
          />

          <SelectInput
            id="twinfield-vat-hoog"
            :label="$t('twinfield.settings.vat_hoog')"
            v-model="form.vat_code_hoog"
            :options="vatOptions"
            :disabled="!form.office_code"
          />
          <SelectInput
            id="twinfield-vat-laag"
            :label="$t('twinfield.settings.vat_laag')"
            v-model="form.vat_code_laag"
            :options="vatOptions"
            :disabled="!form.office_code"
          />
          <SelectInput
            id="twinfield-vat-nul"
            :label="$t('twinfield.settings.vat_nul')"
            v-model="form.vat_code_nul"
            :options="vatOptions"
            :disabled="!form.office_code"
          />

          <SelectInput
            id="twinfield-journal"
            :label="$t('twinfield.settings.journal')"
            v-model="form.journal_code"
            :options="journalOptions"
            :disabled="!form.office_code"
          />
          <div>
            <SelectInput
              id="twinfield-destiny"
              :label="$t('twinfield.settings.destiny')"
              v-model="form.destiny"
              :options="destinyOptions"
            />
            <p class="twinfield-manage__toggle-hint">
              {{ $t('twinfield.settings.destiny_info') }}
            </p>
          </div>

          <div class="twinfield-manage__toggle">
            <ToggleInput
              id="twinfield-is-active"
              :label="$t('twinfield.settings.is_active')"
              v-model="form.is_active"
              :disabled="!canActivate && !form.is_active"
            />
            <p class="twinfield-manage__toggle-hint">
              {{ $t('twinfield.settings.is_active_info') }}
            </p>
          </div>
        </div>

        <p class="twinfield-manage__help">{{ $t('twinfield.settings.help') }}</p>

        <div class="twinfield-manage__actions">
          <Btn variant="danger" :loading="disconnecting" @click="handleDisconnect">
            {{ $t('twinfield.actions.disconnect') }}
          </Btn>
          <div class="twinfield-manage__actions-right">
            <Btn
              v-if="status.reauth_required"
              variant="secondary"
              :loading="connecting"
              @click="handleConnect"
            >
              {{ $t('twinfield.actions.reconnect') }}
            </Btn>
            <Btn variant="primary" :loading="saving" @click="handleSave">
              {{ $t('twinfield.actions.save') }}
            </Btn>
          </div>
        </div>
      </template>
    </template>
  </div>
</template>

<script>
  import { computed, getCurrentInstance, onMounted, reactive, ref } from 'vue';

  import Btn from '@/components/partials/Btn.vue';
  import BaseIcon from '@shared/components/BaseIcon.vue';
  import LoadingSpinner from '@/components/partials/LoadingSpinner.vue';
  import SelectInput from '@/components/inputs/SelectInput.vue';
  import ToggleInput from '@/components/inputs/ToggleInput.vue';

  import twinfieldService from '@/services/twinfieldService';
  import { useTwinfieldStore } from '@/store/twinfieldStore';
  import { useUIStore } from '@/store/uiStore';
  import { useToastStore } from '@/store/toastStore';

  export default {
    name: 'TwinfieldManageModal',

    components: {
      Btn,
      BaseIcon,
      LoadingSpinner,
      SelectInput,
      ToggleInput,
    },

    props: {
      modalId: { type: [String, Number], default: null },
      onUpdated: { type: Function, default: null },
    },

    setup(props) {
      const instance = getCurrentInstance();
      const $t = instance.appContext.config.globalProperties.$t;

      const twinfieldStore = useTwinfieldStore();
      const uiStore = useUIStore();
      const toastStore = useToastStore();

      const loading = ref(false);
      const saving = ref(false);
      const connecting = ref(false);
      const disconnecting = ref(false);
      const status = ref({ connected: false, platform_configured: true });
      const officeOptions = ref([]);
      const ledgerOptions = ref([]);
      const vatOptions = ref([]);
      const journalOptions = ref([]);

      const form = reactive({
        office_code: '',
        journal_code: 'VRK',
        destiny: 'temporary',
        grootboek_omzet_hoog: '',
        grootboek_omzet_laag: '',
        grootboek_omzet_nul: '',
        vat_code_hoog: '',
        vat_code_laag: '',
        vat_code_nul: '',
        is_active: false,
      });

      const isConnected = computed(() => !!status.value.connected);
      const canActivate = computed(
        () => !!form.office_code && !!form.grootboek_omzet_hoog && !!form.vat_code_hoog
      );

      const destinyOptions = computed(() => [
        { value: 'temporary', label: $t('twinfield.settings.destiny_temporary') },
        { value: 'final', label: $t('twinfield.settings.destiny_final') },
      ]);

      const bannerClass = computed(() => {
        if (status.value.reauth_required) return 'twinfield-manage__banner--danger';
        if (status.value.connected && form.is_active) return 'twinfield-manage__banner--success';
        return 'twinfield-manage__banner--info';
      });

      const bannerIcon = computed(() => {
        if (status.value.reauth_required) return 'exclamation-circle';
        if (status.value.connected && form.is_active) return 'check-circle';
        return 'information-circle';
      });

      const bannerMessage = computed(() => {
        if (status.value.platform_configured === false) {
          return $t('twinfield.status.not_configured');
        }
        if (status.value.reauth_required) return $t('twinfield.status.reauth_message');
        if (status.value.connected && form.is_active) return $t('twinfield.status.active_message');
        if (status.value.connected) return $t('twinfield.status.inactive_message');
        return $t('twinfield.status.empty_message');
      });

      const emptyOption = (label) => [{ value: '', label }];

      const mapOptions = (list, emptyLabel) => {
        const mapped = (list || [])
          .filter((item) => item?.code)
          .map((item) => ({
            value: item.code,
            label: item.name && item.name !== item.code ? `${item.code} — ${item.name}` : item.code,
          }));
        return [...emptyOption(emptyLabel), ...mapped];
      };

      const populateFromStatus = (next) => {
        status.value = next || {};
        form.office_code = next?.office_code || '';
        form.journal_code = next?.journal_code || 'VRK';
        form.destiny = next?.destiny === 'final' ? 'final' : 'temporary';
        form.grootboek_omzet_hoog = next?.grootboek_omzet_hoog || '';
        form.grootboek_omzet_laag = next?.grootboek_omzet_laag || '';
        form.grootboek_omzet_nul = next?.grootboek_omzet_nul || '';
        form.vat_code_hoog = next?.vat_code_hoog || '';
        form.vat_code_laag = next?.vat_code_laag || '';
        form.vat_code_nul = next?.vat_code_nul || '';
        form.is_active = !!next?.is_active;
      };

      const notifyUpdated = async () => {
        if (typeof props.onUpdated === 'function') await props.onUpdated();
      };

      const loadOffices = async () => {
        try {
          const data = await twinfieldService.listOffices();
          officeOptions.value = mapOptions(data?.offices, $t('twinfield.settings.office_empty'));
        } catch (error) {
          officeOptions.value = mapOptions([], $t('twinfield.settings.office_empty'));
          toastStore.addToast({
            type: 'error',
            message: error?.response?.data?.error || $t('twinfield.save.error'),
          });
        }
      };

      const loadLookups = async () => {
        if (!form.office_code) {
          ledgerOptions.value = mapOptions([], $t('twinfield.settings.option_empty'));
          vatOptions.value = mapOptions([], $t('twinfield.settings.option_empty'));
          journalOptions.value = mapOptions(
            [{ code: 'VRK', name: 'Verkoop' }],
            $t('twinfield.settings.option_empty')
          );
          return;
        }
        const [ledgers, vatCodes, journals] = await Promise.all([
          twinfieldService.listGrootboekrekeningen().catch(() => ({ grootboekrekeningen: [] })),
          twinfieldService.listVatCodes().catch(() => ({ vat_codes: [] })),
          twinfieldService
            .listJournals()
            .catch(() => ({ journals: [{ code: 'VRK', name: 'Verkoop' }] })),
        ]);
        ledgerOptions.value = mapOptions(
          ledgers?.grootboekrekeningen,
          $t('twinfield.settings.option_empty')
        );
        vatOptions.value = mapOptions(vatCodes?.vat_codes, $t('twinfield.settings.option_empty'));
        journalOptions.value = mapOptions(
          journals?.journals,
          $t('twinfield.settings.option_empty')
        );
      };

      const handleConnect = async () => {
        connecting.value = true;
        try {
          const result = await twinfieldStore.startOAuth();
          if (result?.error === 'popup_blocked') {
            toastStore.addToast({ type: 'info', message: $t('twinfield.connect.popup_blocked') });
            return;
          }
          if (result?.success) {
            populateFromStatus(await twinfieldService.getStatus());
            await loadOffices();
            await loadLookups();
            toastStore.addToast({ type: 'success', message: $t('twinfield.connect.success') });
            await notifyUpdated();
          } else if (result?.error === 'popup_closed') {
            const latest = await twinfieldService.getStatus();
            if (latest?.connected) {
              populateFromStatus(latest);
              await loadOffices();
              await loadLookups();
              await notifyUpdated();
            }
          } else {
            toastStore.addToast({ type: 'error', message: $t('twinfield.connect.failed') });
          }
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message:
              error?.response?.data?.error || error.message || $t('twinfield.connect.failed'),
          });
        } finally {
          connecting.value = false;
        }
      };

      const handleOfficeChange = async (code) => {
        form.office_code = code || '';
        if (!code) return;
        saving.value = true;
        try {
          const result = await twinfieldService.update({ office_code: code, is_active: false });
          populateFromStatus(result?.status);
          form.office_code = code;
          await loadLookups();
          await notifyUpdated();
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: error?.response?.data?.error || $t('twinfield.save.error'),
          });
        } finally {
          saving.value = false;
        }
      };

      const handleSave = async () => {
        if (form.is_active && !canActivate.value) {
          toastStore.addToast({
            type: 'error',
            message: $t('twinfield.settings.activate_required'),
          });
          return;
        }
        saving.value = true;
        try {
          const result = await twinfieldService.update({
            office_code: form.office_code || null,
            journal_code: form.journal_code || 'VRK',
            destiny: form.destiny,
            grootboek_omzet_hoog: form.grootboek_omzet_hoog || null,
            grootboek_omzet_laag: form.grootboek_omzet_laag || null,
            grootboek_omzet_nul: form.grootboek_omzet_nul || null,
            vat_code_hoog: form.vat_code_hoog || null,
            vat_code_laag: form.vat_code_laag || null,
            vat_code_nul: form.vat_code_nul || null,
            is_active: form.is_active,
          });
          populateFromStatus(result?.status);
          toastStore.addToast({ type: 'success', message: $t('twinfield.save.success') });
          await notifyUpdated();
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: error?.response?.data?.error || $t('twinfield.save.error'),
          });
        } finally {
          saving.value = false;
        }
      };

      const handleDisconnect = () => {
        const confirmId = uiStore.addModal(null, {
          title: $t('twinfield.disconnect.title'),
          description: $t('twinfield.disconnect.description'),
          status: 'danger',
          submitText: $t('twinfield.disconnect.confirm'),
          cancelText: $t('common.cancel'),
          onSubmit: async () => {
            disconnecting.value = true;
            try {
              await twinfieldService.disconnect();
              uiStore.closeModal(confirmId);
              if (props.modalId) uiStore.closeModal(props.modalId);
              toastStore.addToast({
                type: 'success',
                message: $t('twinfield.disconnect.success'),
              });
              await notifyUpdated();
            } catch (error) {
              uiStore.closeModal(confirmId);
              toastStore.addToast({
                type: 'error',
                message: error?.response?.data?.error || $t('twinfield.disconnect.error'),
              });
            } finally {
              disconnecting.value = false;
            }
          },
          onCancel: () => uiStore.closeModal(confirmId),
        });
      };

      onMounted(async () => {
        loading.value = true;
        try {
          populateFromStatus(await twinfieldService.getStatus());
          if (status.value.connected) {
            await loadOffices();
            await loadLookups();
          }
        } catch (error) {
          toastStore.addToast({
            type: 'error',
            message: error?.response?.data?.error || $t('twinfield.save.error'),
          });
        } finally {
          loading.value = false;
        }
      });

      return {
        loading,
        saving,
        connecting,
        disconnecting,
        status,
        form,
        isConnected,
        canActivate,
        officeOptions,
        ledgerOptions,
        vatOptions,
        journalOptions,
        destinyOptions,
        bannerClass,
        bannerIcon,
        bannerMessage,
        handleConnect,
        handleOfficeChange,
        handleSave,
        handleDisconnect,
      };
    },
  };
</script>

<style scoped lang="scss">
  .twinfield-manage {
    display: flex;
    flex-direction: column;
    gap: 1rem;

    &__loading {
      display: flex;
      justify-content: center;
      padding: 2rem 0;
    }

    &__banner {
      display: flex;
      align-items: flex-start;
      gap: 0.5rem;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;
      font-size: 0.875rem;
      line-height: 1.45;

      &--info {
        background: var(--slate-50);
        color: var(--slate-700);
      }

      &--success {
        background: var(--green-50);
        color: var(--green-800);
      }

      &--danger {
        background: var(--red-50);
        color: var(--red-800);
      }
    }

    &__company {
      margin: 0;
      font-size: 0.875rem;
      color: var(--slate-600);
    }

    &__fields {
      display: flex;
      flex-direction: column;
      gap: 1rem;
    }

    &__toggle {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;
    }

    &__toggle-hint,
    &__help {
      margin: 0;
      font-size: 0.75rem;
      color: var(--slate-500);
      line-height: 1.4;
    }

    &__help {
      font-size: 0.8125rem;
      padding-top: 0.25rem;
      border-top: 1px solid var(--slate-100);
    }

    &__actions {
      display: flex;
      justify-content: space-between;
      gap: 0.5rem;
      flex-wrap: wrap;
    }

    &__actions-right {
      display: flex;
      gap: 0.5rem;
    }
  }
</style>
